import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { PageLink as Link } from 'src/app/AppAuth/PageLink'
import debounce from 'lodash/debounce'
import { createGlobalStyle } from 'styled-components/macro'
import { SearchChatResult } from '@trustero/trustero-api-web/lib/chat/chat_pb'
import { ChatPreview } from '@trustero/trustero-api-web/lib/chat/chat_pb'
import log from 'loglevel'
import { Spinner } from 'src/Throbber/Spinner'
import { FlexAlign, FlexColumn } from 'src/components/Reusable/Flex'
import formatDate from 'src/Utils/formatDate'
import { ModalForm, ModalFormId } from 'src/components/ModalForms'
import { TrusteroIntelligenceAbsoluteRoutes } from 'src/components/Reusable/RootPage/RootPage.constants'
import { ChatsSVG } from 'src/components/Icons/Basic'
import { ListItemPlaceholder } from 'src/components/Placeholders/ListItemPlaceholder'
import { NewChatIcon } from '../chathistorypanel/chatHistoryPanel.styles'
import { useGetChatHistory } from '../chathistorypanel/chatHistoryPanel.hooks'
import {
  useNavigateToChat,
  useChatPermalink,
} from '../trusteroIntelligence.hooks'
import { useSearchChats } from './chatSearch.hooks'
import {
  ChatSearchModalContent,
  ChatSearchInput,
  ChatSearchResultsContainer,
  ChatSearchResultItem,
  ChatSearchResultTitle,
  ChatSearchResultSnippet,
  ChatSearchEmptyState,
  ChatSearchNewChatRow,
  ChatSearchDateGroup,
  ChatSearchDateLabel,
  ChatSearchChatList,
  ChatSearchChatItem,
  ChatSearchChatIcon,
  ChatSearchModalGlobalStyles,
  ChatSearchLoaderContainer,
  ChatSearchLoaderItem,
} from './chatSearchPopup.styles'

const ChatSearchModalStyles = createGlobalStyle`
  ${ChatSearchModalGlobalStyles}
`

export type ChatSearchPopupProps = {
  isOpen: boolean
  onClose: () => void
}

const ChatSearchResultItemWithLink = ({
  chatId,
  onClick,
  children,
}: {
  chatId: string
  onClick: (e: React.MouseEvent<HTMLAnchorElement>) => void
  children: React.ReactNode
}): JSX.Element => {
  const permalink = useChatPermalink(chatId)
  return (
    <ChatSearchResultItem href={permalink} onClick={onClick}>
      {children}
    </ChatSearchResultItem>
  )
}

type ChatPreviewLocal = {
  id: string
  createdAt: number
  updatedAt: number
  name: string
  ownerId?: string
  isPrivate: boolean
}

type SearchResultLocal = {
  chatId: string
  chatName: string
  updatedAt: number
  snippetHtml?: string
  dataId?: string
}

const chatPreviewReducer = (
  acc: Record<string, ChatPreviewLocal[]>,
  chat: ChatPreview,
) => {
  const updatedAt = chat.getUpdatedAt()
  const dateStr = formatDate(updatedAt, false)
  if (!acc[dateStr]) {
    acc[dateStr] = []
  }
  acc[dateStr].push({
    id: chat.getId(),
    createdAt: chat.getCreatedAt(),
    updatedAt: updatedAt,
    name: chat.getName(),
    ownerId: chat.getOwner()?.getId(),
    isPrivate: chat.getIsPrivate(),
  })
  return acc
}

export const ChatSearchPopup = ({
  isOpen,
  onClose,
}: ChatSearchPopupProps): JSX.Element => {
  const [query, setQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchChatResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const searchChats = useSearchChats()
  const { data: chatHistoryData, isLoading: isLoadingHistory } =
    useGetChatHistory()
  const navigateToChat = useNavigateToChat()
  const inputRef = useRef<HTMLInputElement>(null)
  const params = useParams()

  // Minimum display duration for loader (1.5 seconds)
  const MIN_LOADER_DURATION = 1000

  // Group chats by date
  const chatsByDate = useMemo(() => {
    if (!chatHistoryData) return []
    const chats = chatHistoryData.getChatsList()
    const result: Record<string, ChatPreviewLocal[]> = {}
    const chatsGroupedByDate: Record<string, ChatPreviewLocal[]> = chats.reduce(
      chatPreviewReducer,
      result,
    )

    return Object.entries(chatsGroupedByDate).sort(
      (a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime(),
    )
  }, [chatHistoryData])

  // Group search results by date
  const searchResultsByDate = useMemo(() => {
    if (searchResults.length === 0) return []
    const result: Record<string, SearchResultLocal[]> = {}
    const resultsGroupedByDate: Record<string, SearchResultLocal[]> =
      searchResults.reduce((acc, searchResult) => {
        const chatId = searchResult.getChatId()
        const chatName = searchResult.getChatName() || 'Untitled Chat'
        const updatedAt = searchResult.getUpdatedAt()
        const dateStr = formatDate(updatedAt, false)
        if (!acc[dateStr]) {
          acc[dateStr] = []
        }
        acc[dateStr].push({
          chatId: chatId,
          chatName: chatName,
          updatedAt: updatedAt,
          snippetHtml: searchResult.getSnippetHtml(),
          dataId: searchResult.getDataId() || undefined,
        })
        return acc
      }, result)

    return Object.entries(resultsGroupedByDate).sort(
      (a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime(),
    )
  }, [searchResults])

  // Focus input when popup opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
    }
  }, [isOpen])

  // Reset query when popup closes
  useEffect(() => {
    if (!isOpen) {
      setQuery('')
      setSearchResults([])
      setIsSearching(false)
    }
  }, [isOpen])

  // Debounced search function
  const debouncedSearch = useMemo(
    () =>
      debounce(async (searchQuery: string) => {
        if (!searchQuery.trim()) {
          setSearchResults([])
          setIsSearching(false)
          return
        }

        const startTime = Date.now()
        setIsSearching(true)
        try {
          const response = await searchChats(searchQuery)
          const elapsed = Date.now() - startTime
          const remainingTime = Math.max(0, MIN_LOADER_DURATION - elapsed)

          // Wait for minimum duration before hiding loader
          setTimeout(() => {
            setSearchResults(response.getResultsList())
            setIsSearching(false)
          }, remainingTime)
        } catch (error) {
          log.error('Error searching chats:', error)
          const elapsed = Date.now() - startTime
          const remainingTime = Math.max(0, MIN_LOADER_DURATION - elapsed)

          // Wait for minimum duration before hiding loader even on error
          setTimeout(() => {
            setSearchResults([])
            setIsSearching(false)
          }, remainingTime)
        }
      }, 800),
    [searchChats],
  )

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  // Handle input change
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setQuery(value)
      if (value.trim()) {
        setIsSearching(true) // Set searching state immediately to prevent empty state flash
        debouncedSearch(value)
      } else {
        setSearchResults([])
        setIsSearching(false)
      }
    },
    [debouncedSearch],
  )

  // Handle result click
  const handleResultClick = useCallback(
    (
      e: React.MouseEvent<HTMLAnchorElement>,
      chatId: string,
      dataId?: string,
    ) => {
      e.preventDefault()
      navigateToChat(chatId, dataId)
      onClose()
    },
    [navigateToChat, onClose],
  )

  // Handle recent chat click
  const handleRecentChatClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, chatId: string) => {
      e.preventDefault()
      navigateToChat(chatId)
      onClose()
    },
    [navigateToChat, onClose],
  )

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose],
  )

  const hasQuery = query.trim().length > 0
  const showRecentChats = !hasQuery && !isSearching
  const showResults = hasQuery && !isSearching && searchResults.length > 0
  const showEmptyState = hasQuery && !isSearching && searchResults.length === 0

  return (
    <>
      <ChatSearchModalStyles />
      <ModalForm
        show={isOpen}
        hide={onClose}
        size="lg"
        formId={ModalFormId.CHAT_SEARCH}
        hideButtons={true}
        hideClose={false}
        customHeader={<></>}
        showOverflow={true}
      >
        <ChatSearchModalContent>
          <ChatSearchInput
            ref={inputRef}
            type="text"
            placeholder="Search chats..."
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
          />
          <ChatSearchResultsContainer>
            {isSearching && (
              <ChatSearchLoaderContainer>
                {Array.from({ length: 5 }).map((_, index) => (
                  <ChatSearchLoaderItem key={index}>
                    <ListItemPlaceholder
                      width="100%"
                      height={32}
                      display="block"
                    />
                  </ChatSearchLoaderItem>
                ))}
              </ChatSearchLoaderContainer>
            )}
            {showRecentChats && (
              <>
                {isLoadingHistory ? (
                  <FlexColumn
                    justify={FlexAlign.CENTER}
                    align={FlexAlign.CENTER}
                    style={{ padding: '20px' }}
                  >
                    <Spinner color="primary" size="s" />
                  </FlexColumn>
                ) : chatsByDate.length > 0 ? (
                  <>
                    <ChatSearchNewChatRow
                      as={Link}
                      to={`/${params.pageContext}/${TrusteroIntelligenceAbsoluteRoutes.INDEX}`}
                      onClick={onClose}
                    >
                      <NewChatIcon style={{ width: '12px', height: '12px' }} />
                      <span>New Chat</span>
                    </ChatSearchNewChatRow>
                    {chatsByDate.map(([date, chatsGroup]) => {
                      const sortedChats = [...chatsGroup].sort(
                        (a, b) => b.updatedAt - a.updatedAt,
                      )
                      return (
                        <ChatSearchDateGroup key={date}>
                          <ChatSearchDateLabel>{date}</ChatSearchDateLabel>
                          <ChatSearchChatList>
                            {sortedChats.map((chat) => (
                              <ChatSearchResultItemWithLink
                                key={chat.id}
                                chatId={chat.id}
                                onClick={(e) =>
                                  handleRecentChatClick(e, chat.id)
                                }
                              >
                                <ChatSearchChatItem>
                                  <ChatSearchChatIcon>
                                    <ChatsSVG
                                      style={{ width: '14px', height: '14px' }}
                                    />
                                  </ChatSearchChatIcon>
                                  <ChatSearchResultTitle>
                                    {chat.name}
                                  </ChatSearchResultTitle>
                                </ChatSearchChatItem>
                              </ChatSearchResultItemWithLink>
                            ))}
                          </ChatSearchChatList>
                        </ChatSearchDateGroup>
                      )
                    })}
                  </>
                ) : (
                  <>
                    <ChatSearchNewChatRow
                      as={Link}
                      to={`/${params.pageContext}/${TrusteroIntelligenceAbsoluteRoutes.INDEX}`}
                      onClick={onClose}
                    >
                      <NewChatIcon style={{ width: '12px', height: '12px' }} />
                      <span>New Chat</span>
                    </ChatSearchNewChatRow>
                    <ChatSearchEmptyState>No recent chats</ChatSearchEmptyState>
                  </>
                )}
              </>
            )}
            {showResults &&
              searchResultsByDate.map(([date, resultsGroup]) => {
                const sortedResults = [...resultsGroup].sort(
                  (a, b) => b.updatedAt - a.updatedAt,
                )
                return (
                  <ChatSearchDateGroup key={date}>
                    <ChatSearchDateLabel>{date}</ChatSearchDateLabel>
                    <ChatSearchChatList>
                      {sortedResults.map((result, index) => (
                        <ChatSearchResultItemWithLink
                          key={`${result.chatId}-${date}-${index}`}
                          chatId={result.chatId}
                          onClick={(e) =>
                            handleResultClick(e, result.chatId, result.dataId)
                          }
                        >
                          <ChatSearchChatItem>
                            <ChatSearchChatIcon>
                              <ChatsSVG
                                style={{ width: '14px', height: '14px' }}
                              />
                            </ChatSearchChatIcon>
                            <ChatSearchResultTitle>
                              {result.chatName}
                            </ChatSearchResultTitle>
                          </ChatSearchChatItem>
                          {result.snippetHtml && (
                            <ChatSearchResultSnippet
                              dangerouslySetInnerHTML={{
                                __html: result.snippetHtml,
                              }}
                            />
                          )}
                        </ChatSearchResultItemWithLink>
                      ))}
                    </ChatSearchChatList>
                  </ChatSearchDateGroup>
                )
              })}
            {showEmptyState && (
              <ChatSearchEmptyState>No results found</ChatSearchEmptyState>
            )}
          </ChatSearchResultsContainer>
        </ChatSearchModalContent>
      </ModalForm>
    </>
  )
}
